import { createSlice } from '@reduxjs/toolkit';
import {
  AvailabilitySlot,
  BusinessCalendarDay,
  CheckoutResponse,
  CreateReservationResult,
  FinalizeResponse,
  ManagedReservationView,
  PublicReservationFormView,
} from '../../types/reservation';
import {
  cancelManagedReservation,
  checkoutPublicReservation,
  fetchManagedReservation,
  fetchPublicAvailability,
  fetchPublicBusinessCalendar,
  finalizePublicReservation,
  fetchReservationForm,
  submitPublicReservation,
} from './publicReservationApi';

interface PublicReservationState {
  form: PublicReservationFormView | null;
  formLoading: boolean;
  formError: string | null;
  availability: AvailabilitySlot[];
  availabilityLoading: boolean;
  businessCalendar: BusinessCalendarDay[];
  calendarLoading: boolean;
  submitting: boolean;
  submitError: string | null;
  result: CreateReservationResult | null;
  // ---- 予約デポジット決済（Phase 2・3DS） ----
  /** checkout 応答。clientSecret を含むため決済ステップの Stripe.js へ渡す用途にのみ使い、永続しない */
  checkout: CheckoutResponse | null;
  checkingOut: boolean;
  checkoutError: string | null;
  /** finalize（確定）応答。managementToken/reservationNumber を含む */
  finalizeResult: FinalizeResponse | null;
  finalizing: boolean;
  finalizeError: string | null;
  managed: ManagedReservationView | null;
  managedLoading: boolean;
  managedError: string | null;
  cancelling: boolean;
  cancelError: string | null;
  cancelled: boolean;
}

const initialState: PublicReservationState = {
  form: null,
  formLoading: false,
  formError: null,
  availability: [],
  availabilityLoading: false,
  businessCalendar: [],
  calendarLoading: false,
  submitting: false,
  submitError: null,
  result: null,
  checkout: null,
  checkingOut: false,
  checkoutError: null,
  finalizeResult: null,
  finalizing: false,
  finalizeError: null,
  managed: null,
  managedLoading: false,
  managedError: null,
  cancelling: false,
  cancelError: null,
  cancelled: false,
};

const publicReservationSlice = createSlice({
  name: 'publicReservation',
  initialState,
  reducers: {
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    clearCheckoutError: (state) => {
      state.checkoutError = null;
    },
    clearFinalizeError: (state) => {
      state.finalizeError = null;
    },
    /** checkout をやり直す（期限切れ・カード変更で最初の入力へ戻すとき）。clientSecret も破棄する */
    resetCheckout: (state) => {
      state.checkout = null;
      state.checkoutError = null;
      state.finalizeResult = null;
      state.finalizeError = null;
    },
    /** プレビューや再入場時に状態をリセットする */
    resetPublicReservation: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReservationForm.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(fetchReservationForm.fulfilled, (state, action) => {
        state.form = action.payload;
        state.formLoading = false;
      })
      .addCase(fetchReservationForm.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload as string;
      });

    builder
      .addCase(fetchPublicAvailability.pending, (state) => {
        state.availabilityLoading = true;
      })
      .addCase(fetchPublicAvailability.fulfilled, (state, action) => {
        state.availability = action.payload;
        state.availabilityLoading = false;
      })
      .addCase(fetchPublicAvailability.rejected, (state) => {
        state.availabilityLoading = false;
      });

    builder
      .addCase(fetchPublicBusinessCalendar.pending, (state) => {
        state.calendarLoading = true;
      })
      .addCase(fetchPublicBusinessCalendar.fulfilled, (state, action) => {
        state.businessCalendar = action.payload;
        state.calendarLoading = false;
      })
      .addCase(fetchPublicBusinessCalendar.rejected, (state) => {
        state.calendarLoading = false;
      });

    builder
      .addCase(submitPublicReservation.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitPublicReservation.fulfilled, (state, action) => {
        state.result = action.payload;
        state.submitting = false;
      })
      .addCase(submitPublicReservation.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload as string;
      });

    builder
      .addCase(checkoutPublicReservation.pending, (state) => {
        state.checkingOut = true;
        state.checkoutError = null;
      })
      .addCase(checkoutPublicReservation.fulfilled, (state, action) => {
        state.checkout = action.payload;
        state.checkingOut = false;
      })
      .addCase(checkoutPublicReservation.rejected, (state, action) => {
        state.checkingOut = false;
        state.checkoutError = action.payload as string;
      });

    builder
      .addCase(finalizePublicReservation.pending, (state) => {
        state.finalizing = true;
        state.finalizeError = null;
      })
      .addCase(finalizePublicReservation.fulfilled, (state, action) => {
        state.finalizeResult = action.payload;
        state.finalizing = false;
      })
      .addCase(finalizePublicReservation.rejected, (state, action) => {
        state.finalizing = false;
        state.finalizeError = action.payload as string;
      });

    builder
      .addCase(fetchManagedReservation.pending, (state) => {
        state.managedLoading = true;
        state.managedError = null;
        state.cancelled = false;
      })
      .addCase(fetchManagedReservation.fulfilled, (state, action) => {
        state.managed = action.payload;
        state.managedLoading = false;
      })
      .addCase(fetchManagedReservation.rejected, (state, action) => {
        state.managedLoading = false;
        state.managedError = action.payload as string;
      });

    builder
      .addCase(cancelManagedReservation.pending, (state) => {
        state.cancelling = true;
        state.cancelError = null;
      })
      .addCase(cancelManagedReservation.fulfilled, (state) => {
        state.cancelling = false;
        state.cancelled = true;
        if (state.managed) {
          state.managed.status = 'CANCELLED';
          state.managed.cancellable = false;
        }
      })
      .addCase(cancelManagedReservation.rejected, (state, action) => {
        state.cancelling = false;
        state.cancelError = action.payload as string;
      });
  },
});

export const {
  clearSubmitError,
  clearCheckoutError,
  clearFinalizeError,
  resetCheckout,
  resetPublicReservation,
} = publicReservationSlice.actions;

export default publicReservationSlice.reducer;
